import './Modal.scss'

import { Backdrop, makeStyles, Modal } from '@material-ui/core'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
    Grid,
    GridColumn,
    GridColumnMenuCheckboxFilter,
    GridColumnMenuFilter,
    GridColumnMenuFilterUIProps,
    GridRowClickEvent,
} from '@progress/kendo-react-grid'
import { Popup } from '@progress/kendo-react-popup'
import { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react'

import { useAppSelector } from '../../app/hooks'
import {
    AdvancedPart,
    fetchAdvancedPart,
    FetchAdvancedPartReturnPayload,
} from '../../features/inventory/inventoryAPI'
import {
    fetchPartsAvailability,
    PartsAvailabilityReturnPayload,
} from '../../features/order/orderAPI'
import { AWCButton, BUTTON_TYPES, FastQuoteInput } from '../../GlobalComponents/Inputs/Inputs'
import styles from './AdvancedPartSearchModal.module.scss'

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 0,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        // border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
}))

interface AdvancedPartSearchModalProps {
    close: () => void
    value: string
    setSearchValue: React.Dispatch<React.SetStateAction<string>>
}

export const AdvancedPartSearchModal = (props: AdvancedPartSearchModalProps) => {
    const { close, value, setSearchValue } = props

    const classes = useStyles()
    let customer = useAppSelector((state) => state.customer.currentCustomer!)

    const [advancedPartData, setAdvancedPartData] = useState<AdvancedPart[]>()
    const [inventoryCount, setInventoryCount] = useState<number>()
    const [manufacturerFilterList, setManufacturerFilterList] = useState<string[]>()
    const [vendorCodeFilterList, setVendorCodeFilterList] = useState<string[]>()
    const [loading, setLoading] = useState<boolean>(false)
    const [firstLoad, setFirstLoad] = useState<boolean>(false)

    const [advancedValue, setAdvancedValue] = useState<string>(value)
    const [tempValue, setTempValue] = useState<string>(value)
    const onBlurAdvancedValue = (event: any) => {
        clearFilters()
        setAdvancedValue(event.target.value)
    }

    const [manufacturerFilter, setManufacturerFilter] = useState<string[]>([''])
    const [fullManufactureFilter, setFullManufacturerFilter] =
        useState<CompositeFilterDescriptor | null>(null)
    const [vendorCodeFilter, setVendorCodeFilter] = useState<string[]>([''])
    const [fullVendorCodeFilter, setFullVendorCodeFilter] =
        useState<CompositeFilterDescriptor | null>(null)

    const [descriptionFilter, setDescriptionFilter] = useState<string>('')
    const [fullDescriptionFilter, setFullDescriptionFilter] =
        useState<CompositeFilterDescriptor | null>(null)

    const clearFilters = () => {
        setManufacturerFilter([''])
        setFullManufacturerFilter(null)
        setVendorCodeFilter([''])
        setFullVendorCodeFilter(null)
        setDescriptionFilter('')
        setFullDescriptionFilter(null)
    }

    const handleKeyPress = useCallback((event: any) => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            event.target.blur()
        }
    }, [])

    async function getAdvancedPartData(controller: AbortController) {
        setLoading(true)
        const tempData: FetchAdvancedPartReturnPayload = await fetchAdvancedPart(
            {
                SearchTerm: advancedValue,
                CustomerAccountCode: customer.AccountCode,
                Manufacturers: manufacturerFilter,
                VendorIds: vendorCodeFilter,
                Description: descriptionFilter,
            },
            controller
        )

        return tempData
    }

    useEffect(() => {
        const controller = new AbortController()

        async function changeDropdownData() {
            const tempData: FetchAdvancedPartReturnPayload = await getAdvancedPartData(controller)

            if (tempData) {
                setManufacturerFilterList(tempData.MfgNames)
                setVendorCodeFilterList(tempData.VendorIds)
                setAdvancedPartData(tempData.Inventory)
                setInventoryCount(tempData.InventoryCount)
                setLoading(false)

                if (!firstLoad) {
                    setFirstLoad(true)
                }
            }
        }

        if (advancedValue) {
            changeDropdownData()
        } else {
            setAdvancedPartData([])
            setInventoryCount(undefined)
        }

        return () => {
            controller.abort()
        }
    }, [advancedValue])

    useEffect(() => {
        const controller = new AbortController()

        async function noChangeDropdownData() {
            const tempData: FetchAdvancedPartReturnPayload = await getAdvancedPartData(controller)

            if (tempData) {
                setAdvancedPartData(tempData.Inventory)
                setInventoryCount(tempData.InventoryCount)
                setLoading(false)
            }
        }

        if (firstLoad) {
            noChangeDropdownData()
        }

        return () => {
            controller.abort()
        }
    }, [manufacturerFilter, vendorCodeFilter, descriptionFilter])

    useEffect(() => {
        const tempFilters: string[] = []

        if (fullManufactureFilter && fullManufactureFilter.filters.length > 0) {
            fullManufactureFilter.filters.forEach((filter: any) => {
                filter.filters.forEach((f: any) => {
                    tempFilters.push(f.value)
                })
            })
            setManufacturerFilter(tempFilters)
        } else {
            setManufacturerFilter([''])
        }
    }, [fullManufactureFilter])

    useEffect(() => {
        const tempFilters: string[] = []

        if (fullVendorCodeFilter && fullVendorCodeFilter.filters.length > 0) {
            fullVendorCodeFilter.filters.forEach((filter: any) => {
                filter.filters.forEach((f: any) => {
                    tempFilters.push(f.value)
                })
            })
            setVendorCodeFilter(tempFilters)
        } else {
            setVendorCodeFilter([''])
        }
    }, [fullVendorCodeFilter])

    useEffect(() => {
        let tempDescription: string = ''

        if (fullDescriptionFilter && fullDescriptionFilter.filters.length > 0) {
            fullDescriptionFilter.filters.forEach((filter: any) => {
                tempDescription = filter.filters[0].value
            })

            setDescriptionFilter(tempDescription)
        } else {
            setDescriptionFilter('')
        }
    }, [fullDescriptionFilter])

    const PartNumberCell = (props: any) => {
        return (
            <td style={{ verticalAlign: 'top' }}>
                {props.dataItem.PartNumber}
                <br />
                {props.dataItem.MfgPartNumber && `Mfg: ${props.dataItem.MfgPartNumber}`}
            </td>
        )
    }

    const ManufacturerCheckboxFilter = (props: any) => {
        return (
            <td>
                <GridColumnMenuCheckboxFilter
                    {...props}
                    data={
                        manufacturerFilterList &&
                        manufacturerFilterList.map((str: string) => {
                            return { Manufacturer: str }
                        })
                    }
                    filter={fullManufactureFilter}
                    expanded={true}
                    onFilterChange={(
                        filter: CompositeFilterDescriptor | null,
                        syntheticEvent: SyntheticEvent<any, Event>
                    ) => {
                        setFullManufacturerFilter(filter)
                    }}
                />
            </td>
        )
    }

    // {
    //     "Partkey": 288109,
    //     "PartNumber": "MI TEST",
    //     "Manufacturer": "Instrumentation Misc",
    //     "VendorCode": "MI",
    //     "Description": "Valve Ball QVA SX-SS Series 4.00 Inch Seal Welded 1000 psi FP  \r\nFNPT x FNPT SS CF8M Body 316SS Trim R-TFM Seats\r\nR-TFM Seals NACE (Cut handle to 13.8” from center of stem to\r\nend of handle Tag: P1000227100 REV A)testing data limit for charlie\r\nmiller see",
    //     "Available": 105
    // }

    const VendorCheckboxFilter = (props: any) => {
        return (
            <td>
                <GridColumnMenuCheckboxFilter
                    {...props}
                    data={
                        vendorCodeFilterList &&
                        vendorCodeFilterList.map((str: string) => {
                            return { VendorCode: str }
                        })
                    }
                    // searchBox={}
                    filter={fullVendorCodeFilter}
                    expanded={true}
                    onFilterChange={(
                        filter: CompositeFilterDescriptor | null,
                        syntheticEvent: SyntheticEvent<any, Event>
                    ) => {
                        setFullVendorCodeFilter(filter)
                    }}
                />
            </td>
        )
    }

    const DescriptionFilterCell = (props: any) => {
        return (
            <GridColumnMenuFilter
                {...props}
                filterUI={CustomFilterUI}
                expanded={true}
                onFilterChange={(
                    filter: CompositeFilterDescriptor | null,
                    syntheticEvent: SyntheticEvent<any, Event>
                ) => {
                    setFullDescriptionFilter(filter)
                }}
            />
        )
    }

    const CustomFilterUI = (props: GridColumnMenuFilterUIProps) => {
        const onChange = (event: any) => {
            const value = event.target.value
            const { firstFilterProps } = props

            firstFilterProps.onChange({
                value,
                operator: 'eq',
                syntheticEvent: event.syntheticEvent,
            })
        }

        return (
            <td
                className={'advancedInput'}
                style={{ margin: 25, display: 'flex', justifyContent: 'center' }}
            >
                {/* <FastQuoteInput defaultValue={value} onChange={onChange} /> */}
                <input
                    defaultValue={descriptionFilter}
                    onChange={onChange}
                    style={{ width: 170, fontSize: 16, padding: 5, fontWeight: 300 }}
                />
            </td>
        )
    }

    const GridAvailableCell = (props: any) => {
        const anchor = useRef<HTMLDivElement>(null)
        const [show, setShow] = useState(false)
        const [partsAvailable, setPartsAvailable] = useState<PartsAvailabilityReturnPayload>()
        return (
            <td style={{ verticalAlign: 'top' }}>
                <div
                    style={{
                        textAlign: 'center',
                    }}
                >
                    <div
                        ref={anchor}
                        style={props.dataItem.Available ? { cursor: 'pointer' } : {}}
                        onMouseEnter={async () => {
                            if (props.dataItem.Available) {
                                const availability = await fetchPartsAvailability({
                                    Partkey: props.dataItem.Partkey,
                                })
                                setPartsAvailable(availability)
                                setShow(true)
                            }
                        }}
                        onMouseLeave={() => {
                            setShow(false)
                        }}
                    >
                        {props.dataItem.Available}
                    </div>
                    <Popup
                        show={show}
                        anchor={anchor.current}
                        popupClass={styles.popupContent}
                        anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
                        popupAlign={{ horizontal: 'right', vertical: 'top' }}
                    >
                        <div
                            className={styles.tableBubble}
                            style={{
                                height: '100%',
                                display: 'flex',
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <div style={{ width: '35%' }}>
                                <span style={{ fontWeight: 400 }}>Location #</span>
                                <div
                                    className={'a-divider'}
                                    style={{
                                        marginTop: 10,
                                        marginBottom: 10,
                                    }}
                                />
                                {partsAvailable &&
                                    partsAvailable.LocationAvailability.map((location) => {
                                        return <div>{location.Location}</div>
                                    })}
                            </div>
                            <div
                                style={{
                                    width: '50%',
                                    display: 'inline-block',
                                }}
                            >
                                <span style={{ fontWeight: 400 }}>Location</span>
                                <div
                                    className={'a-divider'}
                                    style={{
                                        marginTop: 10,
                                        marginBottom: 10,
                                    }}
                                />
                                {partsAvailable &&
                                    partsAvailable.LocationAvailability.map((location) => {
                                        return <div>{location.LocationName}</div>
                                    })}
                            </div>
                            <div
                                style={{
                                    width: '15%',
                                    textAlign: 'right',
                                }}
                            >
                                <span
                                    style={{
                                        fontWeight: 400,
                                    }}
                                >
                                    Available
                                </span>
                                <div
                                    className={'a-divider'}
                                    style={{
                                        marginTop: 10,
                                        marginBottom: 10,
                                    }}
                                />
                                {partsAvailable &&
                                    partsAvailable.LocationAvailability.map((location) => {
                                        return <div>{location.Available.toLocaleString()}</div>
                                    })}
                            </div>
                        </div>
                    </Popup>
                </div>
            </td>
        )
    }

    return (
        <Modal
            open={true}
            className={classes.modal}
            // onClose={close}
            BackdropComponent={Backdrop}
            style={{ zIndex: 88 }} // kendo uses 99 for dropdowns
            disableEnforceFocus
        >
            <div className="advancedPartSearch">
                <div className={styles.advancedPartSearchModal}>
                    <div className={styles.advancedPartSearchModalHeader}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <div className={styles.advancedPartSearchModalTitle}>
                                Advanced Parts Search
                            </div>
                            <div onClick={close} className={'a-i-close'} />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <div style={{ display: 'flex' }}>
                                <FastQuoteInput
                                    value={tempValue}
                                    onChange={setTempValue}
                                    onKeyPress={handleKeyPress}
                                    onBlur={onBlurAdvancedValue}
                                    style={{ width: 300 }}
                                    placeholder={'Part Number, Manufacturer, Description...'}
                                    renderEndIcon={() =>
                                        tempValue &&
                                        !loading && (
                                            <div
                                                style={{ width: 17, height: 15 }}
                                                className={'a-i-close'}
                                                onMouseDown={() => {
                                                    setAdvancedValue('')
                                                    setTempValue('')
                                                }}
                                            />
                                        )
                                    }
                                />
                                {!loading && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: 400,
                                        }}
                                    >
                                        {inventoryCount ? (
                                            <span
                                                style={{
                                                    fontSize: 16,
                                                    fontWeight: 500,
                                                    marginLeft: 15,
                                                }}
                                            >
                                                {inventoryCount}
                                                <span style={{ fontWeight: 400, paddingLeft: 5 }}>
                                                    {inventoryCount && inventoryCount > 1
                                                        ? 'results found'
                                                        : 'result found'}
                                                </span>
                                            </span>
                                        ) : (
                                            <span
                                                style={{
                                                    fontSize: 16,
                                                    fontWeight: 400,
                                                    marginLeft: 15,
                                                }}
                                            >
                                                No results found
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <AWCButton
                                type={
                                    (manufacturerFilter.length > 0 && manufacturerFilter[0]) ||
                                    (vendorCodeFilter.length > 0 && vendorCodeFilter[0]) ||
                                    descriptionFilter
                                        ? BUTTON_TYPES.BLUE
                                        : BUTTON_TYPES.SECONDARY
                                }
                                onClick={
                                    (manufacturerFilter.length > 0 && manufacturerFilter[0]) ||
                                    (vendorCodeFilter.length > 0 && vendorCodeFilter[0]) ||
                                    descriptionFilter
                                        ? () => {
                                            clearFilters()
                                        }
                                        : () => {}
                                }
                                style={{ width: 150 }}
                            >
                                Clear Filters
                            </AWCButton>
                        </div>
                    </div>

                    <div className={styles.fullDivider} />

                    {!loading ? (
                        <Grid
                            data={!loading ? advancedPartData : ['']}
                            onRowClick={(event: GridRowClickEvent) => {
                                setSearchValue(event.dataItem.PartNumber)
                                close()
                            }}
                            style={{ height: 700, borderRadius: '0 0 5px 5px' }}
                            filterable={false}
                            sortable={false}
                        >
                            <GridColumn
                                title="Part #"
                                field="PartNumber"
                                width={290}
                                cell={PartNumberCell}
                            />
                            <GridColumn
                                title="Manufacturer"
                                field="Manufacturer"
                                columnMenu={ManufacturerCheckboxFilter}
                                cell={(props: any) => (
                                    <td style={{ verticalAlign: 'top' }}>
                                        {props.dataItem.Manufacturer}
                                    </td>
                                )}
                                width={200}
                            />
                            <GridColumn
                                title="Vendor Id"
                                field="VendorCode"
                                cell={(props: any) => (
                                    <td style={{ verticalAlign: 'top' }}>
                                        {props.dataItem.VendorCode}
                                    </td>
                                )}
                                columnMenu={VendorCheckboxFilter}
                                width={140}
                            />
                            <GridColumn
                                title="Description"
                                field="Description"
                                cell={(props: any) => (
                                    <td style={{ verticalAlign: 'top' }}>
                                        {props.dataItem.Description}
                                    </td>
                                )}
                                columnMenu={DescriptionFilterCell}
                                width={355}
                            />
                            <GridColumn
                                title="Available"
                                field="Available"
                                width={120}
                                cell={GridAvailableCell}
                            />
                        </Grid>
                    ) : (
                        <div style={{ height: 700, width: '100%' }}>
                            <div
                                className={'k-i-loading'}
                                style={{
                                    fontSize: 200,
                                    height: '65vh',
                                    position: 'relative',
                                }}
                            ></div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
